import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  FEEDBACK_LIMITS,
  FeedbackContextValue,
} from '~/shared/utils/feedback-input';

export interface FeedbackDialogProps {
  open: boolean;
  screenshot: string | null;
  context: FeedbackContextValue | null;
  reporterLabel: string | null;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (values: { comment: string; expected: string }) => void;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', flexShrink: 0, width: 72 }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
    >
      {value}
    </Typography>
  </Box>
);

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  screenshot,
  context,
  reporterLabel,
  submitting,
  error,
  onCancel,
  onSubmit,
}) => {
  const [comment, setComment] = useState('');
  const [expected, setExpected] = useState('');

  useEffect(() => {
    if (open) {
      setComment('');
      setExpected('');
    }
  }, [open]);

  const canSubmit = comment.trim().length > 0 && !submitting;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && !submitting) {
          onCancel();
        }
      }}
      maxWidth="sm"
      fullWidth
      data-feedback-ui="dialog"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) {
            onSubmit({ comment: comment.trim(), expected: expected.trim() });
          }
        }}
      >
        <DialogTitle>Leave feedback</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {screenshot ? (
            <Box
              component="img"
              src={screenshot}
              alt="Screenshot of the page with the selected area outlined"
              sx={{
                width: '100%',
                maxHeight: 240,
                objectFit: 'contain',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No screenshot could be captured. Your comment and the page details
              will still be sent.
            </Typography>
          )}

          <TextField
            label="Comment"
            required
            autoFocus
            multiline
            minRows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            inputProps={{ maxLength: FEEDBACK_LIMITS.comment }}
            disabled={submitting}
          />
          <TextField
            label="What did you expect?"
            multiline
            minRows={2}
            value={expected}
            onChange={(event) => setExpected(event.target.value)}
            inputProps={{ maxLength: FEEDBACK_LIMITS.expected }}
            disabled={submitting}
          />

          {context && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Sent with your feedback
              </Typography>
              <DetailRow label="Page" value={context.path} />
              <DetailRow label="Area" value={context.feedbackId} />
              <DetailRow
                label="Viewport"
                value={`${context.viewport.width} × ${context.viewport.height}`}
              />
              {reporterLabel && (
                <DetailRow label="From" value={reporterLabel} />
              )}
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Send feedback
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeedbackDialog;
